import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Grid,
  Header,
  Segment,
  Button,
  Icon,
  Image,
  List,
} from "semantic-ui-react";
import BrokerageFormsList from "./brokerageComponents/BrokerageFormsList";
import BrokerageComplianceManagement from "./brokerageComponents/BrokerageComplianceManagement";
import { fieldConversionMappings } from "../../../app/common/util/formUtils";
import { deadlineTemplatesFormFieldMapping } from "../../deadlineTemplates/deadlineTemplatesComponents/DeadlineTemplatesFormFieldMapping";
import ManagerAnalyticsDashboard from "../managerAnalytics/ManagerAnalyticsDashboard";

export default function BrokerageManagerDashboard() {
	  const { brokerageForms } = useSelector((state) => state.brokerageForms);
	  const { currentUserProfile } = useSelector((state) => state.profile);
    const [showFormFieldInfo, setShowFormFieldInfo] = useState(false);
    const [activeView, setActiveView] = useState("Forms");

  const renderOfficeView = () => {
    return (
      <div>
        <Header color="blue" as="h2">
          Office Information
        </Header>
        <Segment>
          {currentUserProfile?.brokerageLogoRef && (
            <div style={{ marginBottom: "20px" }}>
              <Header as="h3">Brokerage Logo</Header>
              <Image
                src={currentUserProfile.brokerageLogoRef}
                size="medium"
                alt="Brokerage Logo"
              />
            </div>
          )}

          <div style={{ marginBottom: "20px" }}>
            <Header as="h3">Manager</Header>
            <p>
              {currentUserProfile?.firstName} {currentUserProfile?.lastName}
            </p>
          </div>

          {currentUserProfile?.managerAssistants &&
            currentUserProfile.managerAssistants.length > 0 && (
              <div>
                <Header as="h3">Manager Assistants</Header>
                <List>
                  {currentUserProfile.managerAssistants.map(
                    (assistant, index) => (
                      <List.Item key={index}>
                        <List.Icon name="user" />
                        <List.Content>
                          <List.Header>
                            {assistant.name || "Manager Assistant"}
                          </List.Header>
                          {assistant.title && (
                            <List.Description>
                              {assistant.title}
                            </List.Description>
                          )}
                        </List.Content>
                      </List.Item>
                    )
                  )}
                </List>
              </div>
            )}
        </Segment>
      </div>
    );
  };

  const renderFormsView = () => {
    return (
      <div>
        <Header color="blue" as="h2">
          Brokerage Customizations
        </Header>
        <p color="grey">
          Customize your brokerage forms by editing their options and settings.
          <br />
          Select a form from the table below to modify its configuration.
          <br />
        </p>
        <Button
          basic
          color="blue"
          size="small"
          onClick={() => setShowFormFieldInfo((prev) => !prev)}
        >
          How to Set Up Form Fields for TransActioner
          <Icon
            name="info"
            color="blue"
            circular
            inverted
            size="small"
            style={{ marginLeft: "3px", marginBottom: "3px" }}
          />
        </Button>
        {showFormFieldInfo && (
          <div className="top margin">
            <h3>
              How to set up your forms for Auto-Populate, Deadline Extraction,
              and Calculations:
            </h3>
            <p className="text-dark text-normal small vertical margin">
              We're working on enabling self-service form updates, so you'll be
              able to manage your forms directly if you choose.
              <br />
              For now, please email your forms to support@transactioner.com for
              processing. Once processed, you can customize them in this
              section.
              <br />
              How forms should be set up with form fields:
            </p>
            <ol>
              <li>
                Convert radio buttons to checkboxes.
                <i>
                  {" "}
                  Why? Because clearing a radio button is confusing to users.
                  <br />
                  If you name your form fields beginning with "Radiobutton
                  GroupName" and enable Form Validation, then users will be able
                  to run "Validate" on the form to check for more than one
                  selected in a group (e.g., essentially the functionality of a
                  radio button)
                </i>
              </li>
              <li>
                Ensure no form fields with the same name
                <i> (e.g., Adobe will show as #0 and that will not work)</i>
                <br />
                Instead, look for an option with a number 2 after it, like
                "Seller Broker Name 2" to use for the second time that field
                appears in a form and needs to be auto-populated.
              </li>
              <li>
                Checkboxes must be checkboxes and not text fields. Ensure it
                displays when selected with a checkmark (or option typical in
                your state).
              </li>
              <li>
                To enable the calendar pop-up to select dates, the form field
                must begin with "Date".
              </li>
              <li>
                Use fonts: Times New Roman or Helvetica for both the form fields
                and text (ensures font is embedded or available on the user's
                computer)
              </li>
              <li>
                Ensure all form fields use the color of choice for user input.
                For Blue, use #0000CC
              </li>
              <li>Font text size should probably be set to "Auto" or 9.</li>
              <li>
                Ensure there is enough space for the text to be entered in a
                form field.
              </li>
              <li>
                For multi-line fields, ensure the option for multi-line is
                checked for that field. Check for text alignment (left vs
                center).
              </li>
              <li>
                For auto-populate to work, the field name must match the list
                shown below.{" "}
                <i>
                  Logo is automatically added, leave space on top of first page
                  for it if desired.
                </i>
              </li>
              <li>
                Do not have any Adobe-Signature fields in place, as we handle
                signatures within TransActioner.{" "}
              </li>
              <li>
                Ensure all agent signature fields are text fields with the
                correct naming convention (begins with "Signature "), so that
                when the agent types in their name it shows up in cursive.
                <br />
                <i>
                  Note that client signatures are handled separately and should
                  not have any form fields for their signature or date signed
                  areas.
                </i>
              </li>
              <li>
                If a field for auto-population could be either Buyer or Seller
                side, if it is your client then use the Client form fields.
              </li>
              <li>
                For deadline extraction, the field name must match the list
                shown below.
              </li>
              <li>
                To set up an auto-calculate field, the field must begin with
                "CALCULATE " and then type in each field name in the formula.
                <br />
                e.g., "CALCULATE Amount Earnest Money PLUS Amount New Loan PLUS
                Amount Assumption Balance PLUS Amount Private Financing PLUS
                Amount Seller Financing PLUS Amount Item 7 Credit PLUS Amount
                Item 8 Credit PLUS Amount Cash at Closing"
                <br />
                Supported operators are: PLUS, MINUS, TIMES, DIVIDE
              </li>
              <li>Validation Checklist Items? Please contact us to set up.</li>
            </ol>
            <Grid>
              <Grid.Column width={7}>
                <span className="bold text blue mini bottom margin">
                  Form Field Names in the PDF to enable auto-population:
                </span>
                <div
                  style={{
                    maxHeight: "150px",
                    maxWidth: "350px",
                    overflowY: "auto",
                  }}
                >
                  {fieldConversionMappings.map((mapping) => (
                    <React.Fragment key={mapping.pdfFieldName}>
                      {mapping.pdfFieldName}
                      <br />
                    </React.Fragment>
                  ))}
                </div>
              </Grid.Column>
              <Grid.Column width={8}>
                <span className="bold text blue mini bottom margin">
                  Date and Deadline Extractions:
                </span>
                <div
                  style={{
                    maxHeight: "150px",
                    maxWidth: "450px",
                    overflowY: "auto",
                  }}
                >
                  {Object.values(deadlineTemplatesFormFieldMapping).map(
                    (value, index) => (
                      <React.Fragment key={index}>
                        {value}
                        <br />
                      </React.Fragment>
                    )
                  )}
                </div>
              </Grid.Column>
            </Grid>
          </div>
        )}
        <Segment clearing>
          {brokerageForms?.length > 0 ? (
            <BrokerageFormsList
              brokerageForms={brokerageForms}
              column={brokerageForms.column}
              direction={brokerageForms.direction}
            />
          ) : (
            <p>
              There are no brokerage forms configured.
              <br /> <br />
              If you have brokerage-specific forms set up inside TransActioner,
              please contact support@transactioner.com to get set up to access
              your forms here.
              <br /> <br />
              If you want your brokerage-specific forms set up inside
              TransActioner, please email your forms and brokerage name to
              support@transactioner.com.
            </p>
          )}
        </Segment>
      </div>
    );
  };

  const renderStorageView = () => {
    return (
      <div>
        <Header color="blue" as="h2">
          Brokerage Storage
        </Header>
      </div>
    );
  };

  const renderStatsView = () => {
    return <ManagerAnalyticsDashboard />;
  };

  const renderComplianceView = () => {
    return <BrokerageComplianceManagement />;
  };

  return (
    <div className="main-page-wrapper">
      <Grid centered stackable className="small bottom margin">
        <Grid.Column width={14}>
          <Button.Group style={{ marginBottom: "20px" }}>
            <Button
              color={activeView === "Office" ? "blue" : undefined}
              onClick={() => setActiveView("Office")}
            >
              Office
            </Button>
            <Button
              color={activeView === "Forms" ? "blue" : undefined}
              onClick={() => setActiveView("Forms")}
            >
              Forms
            </Button>
            {currentUserProfile?.role === "manager" && (
              <Button
                color={activeView === "Compliance" ? "blue" : undefined}
                onClick={() => setActiveView("Compliance")}
              >
                Compliance
              </Button>
            )}
            {currentUserProfile?.role === "manager" && (
              <Button
                color={activeView === "Stats" ? "blue" : undefined}
                onClick={() => setActiveView("Stats")}
              >
                Stats
              </Button>
            )}
            <Button
              color={activeView === "Storage" ? "blue" : undefined}
              onClick={() => setActiveView("Storage")}
            >
              Storage
            </Button>
          </Button.Group>

          {activeView === "Office" && renderOfficeView()}
          {activeView === "Forms" && renderFormsView()}
          {activeView === "Compliance" && renderComplianceView()}
          {activeView === "Stats" && renderStatsView()}
          {activeView === "Storage" && renderStorageView()}
        </Grid.Column>
      </Grid>
    </div>
  );
}
